/**
 * GroupCard — one group in the editor's left panel. Collapsed it shows the
 * head row (swatch, name, area count, visibility toggle, + Add areas); expanded
 * it reveals the manual area-entry UI specified in the mock-up addendum §3:
 *
 *   - a .segmented type selector (ZIP | City | County | State | Tract | Radius;
 *     "Drawn" is intentionally absent — drawn areas only come from the canvas
 *     draw tools, not typed entry),
 *   - a multi-entry <textarea> (";" or newline separated) for most types, or two
 *     inputs (place + miles) for Radius,
 *   - an "Add areas" action that calls POST /api/geo/boundaries and folds the
 *     results into the group,
 *   - item chips — county chips show "{name}, {state} — {GEOID}", others are
 *     label-only,
 *   - a "Copy codes" action (visible when any area has a GEOID) that copies all
 *     the group's GEOIDs comma-separated,
 *   - inline disambiguation when the API returns { status: "ambiguous",
 *     candidates: [...] } — the row offers the candidates instead of silently
 *     picking the first (QA D1).
 *
 * All mutations bubble up to the Editor via callbacks; this component holds only
 * transient UI state (which type is selected, the textarea contents, pending
 * disambiguations). The Editor owns the map document and autosave.
 *
 * For radius/drawn groups only, a `.group-summary` line renders under the head row
 * showing size + population ("10 mi radius · 45,231 population" / "12.4 sq mi ·
 * 28,904 population"), from the `metrics` prop (Editor's useShapeMetrics). While
 * the population query is in flight it reads "Calculating…"; census-backed groups
 * (ZIP/city/county/state/tract) get no summary at all (metrics is undefined for
 * them). See the approved mock-up (03-editor-shape-labels.html).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Eye,
  EyeOff,
  MoreVertical,
  Plus,
  Copy,
  Pencil,
  X,
  ChevronDown,
  ChevronUp,
  Loader2,
  CircleDot,
  Shapes,
  Users,
  BarChart3,
  RefreshCw,
} from 'lucide-react';
import api from '../utils/api';
import { formatNumber, areaLabel } from '../utils/format';
import { useToast } from '../context/ToastContext';

// Typed area types offered in the segmented selector. "drawn" is excluded (see
// header). Order matches the addendum's ASCII diagram.
const TYPE_OPTIONS = [
  { value: 'zip', label: 'ZIP' },
  { value: 'city', label: 'City' },
  { value: 'county', label: 'County' },
  { value: 'state', label: 'State' },
  { value: 'tract', label: 'Tract' },
  { value: 'radius', label: 'Radius' },
];

// Type-specific placeholder/help copy, carried over from the prototype
// (map-builder.html) so entry hints match what users already know.
const PLACEHOLDERS = {
  zip: 'e.g. 43004; 43230; 43081',
  city: 'e.g. Columbus, OH; Dublin, OH',
  county: 'e.g. Franklin, OH; Delaware, OH',
  state: 'e.g. Ohio; Indiana; KY',
  tract: 'e.g. 39049001100; 39049001200',
};

// Split a multi-entry field on ";" or newlines, trimming and dropping blanks.
function splitEntries(raw) {
  return raw
    .split(/[;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// Mint a client-side id for a newly-added area. The server owns final ids on
// save (document-style autosave); this only needs to be stable for React keys
// and optimistic rendering until the next PUT round-trips.
function tempId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

// Build an area object from a resolved geo-boundaries result (or a chosen
// disambiguation candidate). Census-backed areas carry geoid + boundary_id but
// no geometry (server resolves geometry from boundary_id — see the contract).
function areaFromResolved(areaType, resolved, sortOrder) {
  const label =
    areaType === 'county' && resolved.name
      ? resolved.state
        ? `${resolved.name}, ${resolved.state}`
        : resolved.name
      : resolved.name || resolved.entry || '';
  return {
    id: tempId(),
    area_type: areaType,
    label,
    geoid: resolved.geoid || null,
    boundary_id: resolved.boundary_id || null,
    geometry: {},
    params: {},
    sort_order: sortOrder,
    // Kept for chip rendering; not part of the persisted shape but harmless.
    _state: resolved.state || null,
  };
}

export default function GroupCard({
  group,
  mapId,
  metrics,
  expanded,
  selected,
  onToggleExpand,
  onUpdateGroup,
  onAddAreas,
  onRemoveArea,
  onDeleteGroup,
}) {
  const { showToast } = useToast();

  const [areaType, setAreaType] = useState('zip');
  const [entryText, setEntryText] = useState('');
  const [radiusPlace, setRadiusPlace] = useState('');
  const [radiusMiles, setRadiusMiles] = useState('');
  const [adding, setAdding] = useState(false);
  // Pending disambiguations: [{ entry, candidates: [...] }]. Rendered inline so
  // the user resolves each before the entries are committed.
  const [ambiguous, setAmbiguous] = useState([]);
  const [notFound, setNotFound] = useState([]);
  const [kebabOpen, setKebabOpen] = useState(false);
  // Which tab of the expanded card is shown: 'areas' (manual entry, default) or
  // 'demographics' (aggregate ACS stats — addendum §2).
  const [activeTab, setActiveTab] = useState('areas');
  // Inline group rename — same mechanics as Editor's map-title rename
  // (autoFocus, commit on blur/Enter, discard on Escape), triggered from the
  // kebab menu rather than an always-visible pencil icon: the group-head row
  // is already dense (swatch, name, count, 3 icon buttons), so a persistent
  // extra icon here would be clutter the spacious map-title topbar doesn't have.
  const [renamingGroup, setRenamingGroup] = useState(false);
  const [groupNameDraft, setGroupNameDraft] = useState('');

  // Stabilize the areas reference so the memos below don't recompute every
  // render (group.areas || [] would mint a new array each time).
  const areas = useMemo(() => group.areas || [], [group.areas]);
  const hasCodes = useMemo(() => areas.some((a) => a.geoid), [areas]);

  const areaCountLabel = useMemo(() => {
    const n = areas.length;
    // Mirror the mock-up's "86 ZIPs" / "1 area" phrasing: if every area shares a
    // type, pluralize that type; otherwise fall back to a generic "areas".
    if (n === 0) return 'Empty';
    const types = new Set(areas.map((a) => a.area_type));
    if (types.size === 1) {
      const t = [...types][0];
      const noun = { zip: 'ZIP', city: 'city', county: 'county', state: 'state', tract: 'tract', radius: 'radius', drawn: 'drawn area' }[t] || 'area';
      if (t === 'city') return `${n} ${n === 1 ? 'city' : 'cities'}`;
      if (t === 'county') return `${n} ${n === 1 ? 'county' : 'counties'}`;
      return `${n} ${noun}${n === 1 ? '' : 's'}`;
    }
    return `${n} area${n === 1 ? '' : 's'}`;
  }, [areas]);

  // Resolve typed entries against the geo service and fold results into the
  // group. Ambiguous/not-found entries are surfaced inline rather than dropped.
  const handleAddAreas = async () => {
    if (adding) return;

    // Radius is a special two-field case: place + miles → geocode happens
    // server-side when the area is materialized. We store the raw params here;
    // the boundaries endpoint isn't used for radius (it has no boundary GEOID).
    if (areaType === 'radius') {
      const miles = parseFloat(radiusMiles);
      if (!radiusPlace.trim() || !Number.isFinite(miles) || miles <= 0) {
        showToast('Enter a place and a positive radius in miles.', { variant: 'error' });
        return;
      }
      setAdding(true);
      try {
        // Geocode the center so the canvas can draw the circle immediately.
        const geo = await api.get(`/geo/geocode?q=${encodeURIComponent(radiusPlace.trim())}`);
        const area = {
          id: tempId(),
          area_type: 'radius',
          label: `${radiusPlace.trim()} — ${miles} mi`,
          geoid: null,
          boundary_id: null,
          geometry: {},
          params: { center: [geo.lat, geo.lon], radius_mi: miles },
          sort_order: areas.length,
        };
        onAddAreas(group.id, [area]);
        setRadiusPlace('');
        setRadiusMiles('');
        showToast('Added radius area');
      } catch (err) {
        if (err.status === 404) {
          showToast(`Couldn’t find "${radiusPlace.trim()}".`, { variant: 'error' });
        } else {
          showToast(err.message || 'Could not add radius.', { variant: 'error' });
        }
      } finally {
        setAdding(false);
      }
      return;
    }

    const entries = splitEntries(entryText);
    if (entries.length === 0) {
      showToast('Enter at least one area.', { variant: 'error' });
      return;
    }

    setAdding(true);
    setAmbiguous([]);
    setNotFound([]);
    try {
      const results = await api.post('/geo/boundaries', { area_type: areaType, entries });
      const list = Array.isArray(results) ? results : results?.results || [];

      const resolvedAreas = [];
      const pendingAmbiguous = [];
      const pendingNotFound = [];

      list.forEach((r, i) => {
        if (r.status === 'ok') {
          resolvedAreas.push(areaFromResolved(areaType, r, areas.length + resolvedAreas.length));
        } else if (r.status === 'ambiguous') {
          pendingAmbiguous.push({ entry: r.entry, candidates: r.candidates || [] });
        } else {
          pendingNotFound.push(r.entry || entries[i]);
        }
      });

      if (resolvedAreas.length > 0) {
        onAddAreas(group.id, resolvedAreas);
      }
      setAmbiguous(pendingAmbiguous);
      setNotFound(pendingNotFound);

      // Clear only the entries that fully resolved; keep the box if everything
      // needs disambiguation so the user still sees what they typed.
      if (pendingAmbiguous.length === 0 && pendingNotFound.length === 0) {
        setEntryText('');
        showToast(`Added ${resolvedAreas.length} ${resolvedAreas.length === 1 ? 'area' : 'areas'}`);
      }
    } catch (err) {
      showToast(err.message || 'Could not resolve areas.', { variant: 'error' });
    } finally {
      setAdding(false);
    }
  };

  // Resolve one ambiguous entry by picking a candidate → commit it as an area
  // and drop it from the pending list.
  const chooseCandidate = (entry, candidate) => {
    const area = areaFromResolved(areaType, { ...candidate, entry }, areas.length);
    onAddAreas(group.id, [area]);
    setAmbiguous((prev) => prev.filter((a) => a.entry !== entry));
  };

  const dismissAmbiguous = (entry) => {
    setAmbiguous((prev) => prev.filter((a) => a.entry !== entry));
  };

  // Copy all GEOIDs in the group to the clipboard, comma-separated.
  const handleCopyCodes = async () => {
    const codes = areas.map((a) => a.geoid).filter(Boolean);
    if (codes.length === 0) return;
    const text = codes.join(', ');
    try {
      await navigator.clipboard.writeText(text);
      showToast(`Copied ${codes.length} ${codes.length === 1 ? 'code' : 'codes'}`);
    } catch {
      // Clipboard API can be blocked (insecure context / permissions). Fall back
      // to a hidden textarea + execCommand so the action still works.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        showToast(`Copied ${codes.length} codes`);
      } catch {
        showToast('Copy failed — select the codes manually.', { variant: 'error' });
      }
      document.body.removeChild(ta);
    }
  };

  const currentType = areaType;

  const startRenameGroup = () => {
    setGroupNameDraft(group.name || '');
    setRenamingGroup(true);
  };
  const commitRenameGroup = () => {
    const name = groupNameDraft.trim() || 'Untitled group';
    onUpdateGroup(group.id, { name });
    setRenamingGroup(false);
  };

  return (
    <div className={`card group-card${selected ? ' is-selected' : ''}`}>
      <div className="group-head">
        <span className="swatch" style={{ background: group.color || '#2563EB' }} />
        {renamingGroup ? (
          <input
            className="input group-name-input"
            value={groupNameDraft}
            autoFocus
            onChange={(e) => setGroupNameDraft(e.target.value)}
            onBlur={commitRenameGroup}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRenameGroup();
              if (e.key === 'Escape') setRenamingGroup(false);
            }}
          />
        ) : (
          <button
            className="group-name group-name-btn"
            onClick={() => onToggleExpand(group.id)}
            title={expanded ? 'Collapse' : 'Expand to add areas'}
          >
            {group.name || 'Untitled group'}
          </button>
        )}
        <span className="group-count">{areaCountLabel}</span>
        <div className="group-actions">
          <button
            className="btn-icon"
            title="Add areas"
            onClick={() => onToggleExpand(group.id)}
          >
            <Plus size={13} />
          </button>
          <button
            className="btn-icon"
            title={group.is_visible === false ? 'Show group' : 'Hide group'}
            onClick={() => onUpdateGroup(group.id, { is_visible: group.is_visible === false })}
          >
            {group.is_visible === false ? <EyeOff size={13} /> : <Eye size={13} />}
          </button>
          <div className="kebab-wrap">
            <button
              className="btn-icon"
              title="More"
              onClick={() => setKebabOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={kebabOpen}
            >
              <MoreVertical size={13} />
            </button>
            {kebabOpen && (
              <div className="kebab-menu" role="menu" onMouseLeave={() => setKebabOpen(false)}>
                <button
                  className="kebab-item"
                  role="menuitem"
                  onClick={() => {
                    setKebabOpen(false);
                    startRenameGroup();
                  }}
                >
                  <Pencil size={13} /> Rename group
                </button>
                <button
                  className="kebab-item danger"
                  role="menuitem"
                  onClick={() => {
                    setKebabOpen(false);
                    onDeleteGroup(group.id);
                  }}
                >
                  <X size={13} /> Delete group
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Size + population summary — radius/drawn groups only (metrics is
          undefined for census-backed groups). While the population query is in
          flight, show "Calculating…" rather than blocking the card. */}
      {metrics && metrics.sizeLabel && (
        <div className="group-summary">
          {metrics.kind === 'radius' ? <CircleDot size={12} /> : <Shapes size={12} />}
          {metrics.sizeLabel}
          <span className="sep">·</span>
          <Users size={12} />
          {metrics.loading ? (
            'Calculating…'
          ) : metrics.error || metrics.population == null ? (
            '— population'
          ) : (
            `${formatNumber(metrics.population)} population${metrics.truncated ? '+' : ''}`
          )}
        </div>
      )}

      {/* Collapsed: compact chip preview (first few areas). Expanded: full entry
          UI + full chip list. */}
      {!expanded && areas.length > 0 && (
        <div className="group-items">
          {areas.slice(0, 5).map((a) => (
            <span key={a.id} className="item-chip">
              {areaLabel(a)}
            </span>
          ))}
          {areas.length > 5 && <span className="item-chip">+{areas.length - 5} more</span>}
        </div>
      )}

      {expanded && (
        <div className="group-expand">
          {/* Areas / Demographics tabs (addendum §2). Areas is the manual-entry
              UI; Demographics shows aggregate ACS stats for the group. */}
          <div className="tabs group-tabs" role="tablist" aria-label="Group detail">
            <button
              className={`tab${activeTab === 'areas' ? ' is-active' : ''}`}
              role="tab"
              aria-selected={activeTab === 'areas'}
              onClick={() => setActiveTab('areas')}
            >
              Areas
            </button>
            <button
              className={`tab${activeTab === 'demographics' ? ' is-active' : ''}`}
              role="tab"
              aria-selected={activeTab === 'demographics'}
              onClick={() => setActiveTab('demographics')}
            >
              <BarChart3 size={13} /> Demographics
            </button>
          </div>

          {activeTab === 'demographics' && (
            <DemographicsTab mapId={mapId} group={group} areas={areas} />
          )}

          {activeTab === 'areas' && (
          <>
          {/* Type selector */}
          <div className="segmented group-type-seg" role="group" aria-label="Area type">
            {TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`segmented-option${currentType === opt.value ? ' is-active' : ''}`}
                onClick={() => setAreaType(opt.value)}
                aria-pressed={currentType === opt.value}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Entry field: two inputs for radius, textarea for everything else. */}
          {currentType === 'radius' ? (
            <div className="radius-fields">
              <input
                className="input"
                type="text"
                placeholder="Center place or address"
                value={radiusPlace}
                onChange={(e) => setRadiusPlace(e.target.value)}
              />
              <input
                className="input radius-miles"
                type="number"
                min="0"
                step="0.5"
                placeholder="Miles"
                value={radiusMiles}
                onChange={(e) => setRadiusMiles(e.target.value)}
              />
            </div>
          ) : (
            <>
              <textarea
                className="textarea group-entry"
                placeholder={PLACEHOLDERS[currentType]}
                value={entryText}
                onChange={(e) => setEntryText(e.target.value)}
                rows={2}
              />
              {/* Helper explains the multi-entry rule rather than repeating the
                  placeholder example verbatim (UI/UX review N2). */}
              <div className="field-helper">
                Enter several at once — separate them with a semicolon or a new line.
              </div>
            </>
          )}

          <div className="group-expand-actions">
            <button
              className="btn btn-primary btn-sm"
              onClick={handleAddAreas}
              disabled={adding}
            >
              {adding ? <Loader2 size={13} className="spin" /> : <Plus size={13} />} Add areas
            </button>
          </div>

          {/* Inline disambiguation (QA D1) */}
          {ambiguous.map((amb) => (
            <div key={amb.entry} className="callout callout-warning disambig" role="alert">
              <div style={{ flex: 1 }}>
                <div className="disambig-q">
                  “{amb.entry}” matches several places — pick one or add a state:
                </div>
                <div className="disambig-options">
                  {amb.candidates.map((c) => (
                    <button
                      key={c.geoid}
                      className="pill disambig-pill"
                      onClick={() => chooseCandidate(amb.entry, c)}
                    >
                      {c.name}{c.state ? `, ${c.state}` : ''}
                    </button>
                  ))}
                  <button className="btn btn-ghost btn-sm" onClick={() => dismissAmbiguous(amb.entry)}>
                    Skip
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Not-found entries */}
          {notFound.length > 0 && (
            <div className="callout callout-error disambig" role="alert">
              <div>
                Couldn’t find: {notFound.join(', ')}. Check spelling or add a state.
              </div>
            </div>
          )}

          {/* Full chip list + copy codes */}
          {areas.length > 0 && (
            <>
              <div className="group-items group-items-full">
                {areas.map((a) => (
                  <span key={a.id} className="item-chip item-chip-removable">
                    {areaLabel(a)}
                    <button
                      className="chip-remove"
                      title="Remove"
                      onClick={() => onRemoveArea(group.id, a.id)}
                      aria-label={`Remove ${a.label}`}
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
              {hasCodes && (
                <div className="group-expand-actions">
                  <button className="btn btn-ghost btn-sm" onClick={handleCopyCodes}>
                    <Copy size={13} /> Copy codes
                  </button>
                </div>
              )}
            </>
          )}
          </>
          )}

          <button className="group-collapse-link" onClick={() => onToggleExpand(group.id)}>
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Collapse
          </button>
        </div>
      )}
    </div>
  );
}

// Census-backed area types ACS demographics apply to. Radius/drawn areas have no
// GEOID and no ACS row, so a group of only those has no demographics.
const CENSUS_AREA_TYPES = new Set(['zip', 'city', 'county', 'state', 'tract']);

// Server-assigned UUID shape. A freshly-created group carries a client temp id
// (crypto.randomUUID or "tmp_…") until the first autosave round-trips real ids.
// The demographics endpoint keys on the SAVED group id, so we only fetch for a
// group whose id is a real UUID *and* isn't a client-minted "tmp_" id.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The five aggregate metrics rendered in the Demographics tab, in display order.
// `money`/`pct` drive formatting; `label` is the row label.
const DEMO_METRICS = [
  { key: 'median_hh_income', label: 'Median household income', money: true },
  { key: 'households', label: 'Households' },
  { key: 'owner_occupied_pct', label: 'Owner-occupied', pct: true },
  { key: 'median_age', label: 'Median age' },
  { key: 'median_home_value', label: 'Median home value', money: true },
];

// Content signature of a group's census areas — changes when census areas are
// added/removed so the tab refetches, but NOT when an unrelated autosave re-mints
// ids (we key on geoids, not group/area ids).
function censusSignature(areas) {
  return (areas || [])
    .filter((a) => CENSUS_AREA_TYPES.has(a.area_type) && a.geoid)
    .map((a) => a.geoid)
    .sort()
    .join(',');
}

/**
 * DemographicsTab — aggregate ACS stats for a group, fetched on demand from
 * GET /api/maps/:id/groups/:groupId/demographics (addendum §2). Handles the four
 * states the spec calls for:
 *   - loading  → skeleton rows (design-system .skeleton)
 *   - error    → inline message with a retry (ACS is best-effort; never blocks)
 *   - empty    → group has no census areas (radius/drawn only, or brand new)
 *   - data     → label/value rows; a metric with no data shows "—" per the spec
 *
 * Fetches only for a persisted group (real UUID id) with at least one census
 * area. Refetches when the census-area set changes; a manual refresh button
 * re-pulls (demographics are cache-first server-side, so this is cheap on a hit).
 */
function DemographicsTab({ mapId, group, areas }) {
  const [state, setState] = useState({ status: 'idle', data: null });

  const censusAreas = useMemo(
    () => (areas || []).filter((a) => CENSUS_AREA_TYPES.has(a.area_type) && a.geoid),
    [areas]
  );
  const signature = useMemo(() => censusSignature(areas), [areas]);
  // A real, saved group id is required to hit the endpoint (see UUID_RE note).
  const groupPersisted = typeof group.id === 'string' && UUID_RE.test(group.id) && !group.id.startsWith('tmp_');
  const canFetch = Boolean(mapId && groupPersisted && censusAreas.length > 0);

  const load = useCallback(async () => {
    if (!canFetch) return;
    setState({ status: 'loading', data: null });
    try {
      const data = await api.get(`/maps/${mapId}/groups/${group.id}/demographics`);
      setState({ status: 'done', data });
    } catch (err) {
      setState({ status: 'error', data: null, message: err.message });
    }
    // group.id / mapId identify the endpoint; signature forces a reload when the
    // census-area set changes even though the id is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId, group.id, canFetch, signature]);

  useEffect(() => {
    load();
  }, [load]);

  // Empty: no census areas to aggregate (radius/drawn-only group, or one still
  // saving). Distinguish "still saving" from "genuinely no census data".
  if (censusAreas.length === 0) {
    return (
      <div className="demographics-tab">
        <p className="demographics-empty">
          {areas.length > 0
            ? 'No demographic data — this group has only radius or drawn areas.'
            : 'Add ZIPs, cities, counties, or tracts to see demographics.'}
        </p>
      </div>
    );
  }
  if (!groupPersisted) {
    return (
      <div className="demographics-tab">
        <p className="demographics-empty">
          <Loader2 size={13} className="spin" /> Saving the group — demographics load once it saves.
        </p>
      </div>
    );
  }

  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <div className="demographics-tab" aria-busy="true">
        {DEMO_METRICS.map((m) => (
          <div key={m.key} className="demographics-row">
            <span className="demographics-label">{m.label}</span>
            <span className="skeleton skeleton-line demographics-skel" />
          </div>
        ))}
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="demographics-tab">
        <div className="callout callout-error" role="alert">
          <div>Couldn’t load demographics. {state.message || ''}</div>
        </div>
        <div className="group-expand-actions">
          <button className="btn btn-ghost btn-sm" onClick={load}>
            <RefreshCw size={13} /> Retry
          </button>
        </div>
      </div>
    );
  }

  const d = state.data || {};
  // Coverage note: how many of the group's census areas had ACS data. When some
  // are missing we say so, so a partial aggregate isn't mistaken for the whole.
  const withData = typeof d.areas_with_data === 'number' ? d.areas_with_data : null;
  const total = typeof d.area_count === 'number' ? d.area_count : censusAreas.length;

  return (
    <div className="demographics-tab">
      {DEMO_METRICS.map((m) => (
        <div key={m.key} className="demographics-row">
          <span className="demographics-label">{m.label}</span>
          <span className="demographics-value">{formatMetric(d[m.key], m)}</span>
        </div>
      ))}
      <div className="demographics-foot">
        {withData != null && withData < total ? (
          <span className="demographics-coverage">
            Based on {formatNumber(withData)} of {formatNumber(total)} areas with data.
          </span>
        ) : (
          <span className="demographics-coverage">Weighted across {formatNumber(total)} areas.</span>
        )}
        <button className="btn btn-ghost btn-sm" onClick={load} title="Refresh demographics">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
    </div>
  );
}

/**
 * Format one aggregate metric for display. null/undefined → "—" (addendum §2:
 * show a dash for unavailable data). Money is a whole-dollar currency string;
 * percent appends "%"; plain numbers get thousands separators.
 *
 * @param {number|null} value
 * @param {{money?: boolean, pct?: boolean}} meta
 * @returns {string}
 */
function formatMetric(value, meta) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  if (meta.money) {
    return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  }
  if (meta.pct) {
    return `${n.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
  }
  return formatNumber(n);
}

